import { ArrowRight } from "lucide-react";
import { LocaleLink } from "@/components/site/LocaleLink";
import type { RelatedService } from "@/content/posts/types";
import { useT } from "@/lib/i18n/I18nProvider";

const BG_SERVICES: Record<string, { title: string; description: string }> = {
  "/media-monitoring": {
    title: "Медиен мониторинг",
    description:
      "Проследявайте всяко споменаване в онлайн, печат, телевизия и социални мрежи - в един общ поток.",
  },
  "/media-monitoring/platform": {
    title: "Платформа за медиен мониторинг",
    description:
      "Разгледайте информационните табла, известията, отчетите и AI анализите в MLP 2.0.",
  },
  "/request-a-demo": {
    title: "Заявете демонстрация",
    description:
      "Вижте MLP 2.0 с данни за Вашия бранд в рамките на фокусирана 30-минутна демонстрация.",
  },
  "/media-analysis": {
    title: "Медиен анализ",
    description:
      "Превърнете суровите публикации в стратегически изводи чрез нашия хибриден AI и аналитичен подход.",
  },
  "/media-analysis/crisis-monitoring": {
    title: "Мониторинг на кризи",
    description:
      "Засичайте репутационни рискове в момента на възникването им с известия в реално време и 24/7 покритие.",
  },
  "/media-analysis/reputation": {
    title: "Анализ на репутацията",
    description:
      "Измервайте нагласи, дял от гласа и наративни промени, за да насочвате разговора.",
  },
  "/media-analysis/competitive-intelligence": {
    title: "Конкурентен анализ",
    description:
      "Сравнявайте бранда си с конкурентите и откривайте възможности преди тях.",
  },
  "/media-analysis/employer-branding": {
    title: "Анализ на работодателската организация",
    description:
      "Разберете как кандидатите и служителите възприемат бранда ви като работодател.",
  },
  "/media-analysis/market-research": {
    title: "Пазарно проучване",
    description:
      "Откривайте пазарни тенденции, нужди на аудиторията и нови възможности от медийните данни.",
  },
  "/annual-media-analysis": {
    title: "Годишен медиен анализ",
    description:
      "Годишен задълбочен преглед на вашето медийно присъствие, теми и конкурентна позиция.",
  },
  "/industries/automotive": {
    title: "Автомобилна индустрия",
    description:
      "Медиен мониторинг и анализ за автомобилни компании и брандове за мобилност в Югоизточна Европа.",
  },
  "/industries/pharma": {
    title: "Фармацевтична индустрия",
    description:
      "Медиен мониторинг и анализ за фармацевтични компании и здравни брандове.",
  },
};

export function RelatedServices({ items }: { items: RelatedService[] }) {
  const { locale } = useT();
  const isBg = locale === "bg";
  if (!items || items.length === 0) return null;
  return (
    <section
      aria-labelledby="related-services-heading"
      className="mt-16 rounded-2xl border border-border/60 bg-secondary/40 p-6 lg:p-8"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
        {isBg ? "Свързани услуги" : "Related services"}
      </p>
      <h2
        id="related-services-heading"
        className="font-display mt-2 text-[clamp(1.4rem,2vw,1.75rem)] font-medium leading-tight text-foreground"
      >
        {isBg ? "Приложете тези изводи на практика" : "Put these insights to work"}
      </h2>
      <p className="mt-2 max-w-2xl text-[14px] text-foreground/70">
        {isBg
          ? "Разгледайте услугите на Perceptica, които ви помагат да действате по прочетеното."
          : "Explore the Perceptica services that help you act on what you just read."}
      </p>

      <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((s) => {
          const tr = isBg ? BG_SERVICES[s.href] : undefined;
          const title = tr?.title ?? s.title;
          const description = tr?.description ?? s.description;
          return (
          <li key={s.href}>
            <LocaleLink
              to={s.href}
              className="group flex h-full flex-col rounded-xl border border-border/60 bg-background p-5 transition-colors hover:border-accent/60 hover:bg-background"
            >
              <h3 className="font-display text-[16px] font-medium text-foreground">
                {title}
              </h3>
              <p className="mt-2 flex-1 text-[13px] leading-relaxed text-foreground/70">
                {description}
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-accent">
                {isBg ? `Научете повече за ${title}` : `Learn more about ${title}`}
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
              </span>
            </LocaleLink>
          </li>
          );
        })}
      </ul>
    </section>
  );
}
